/**
 * Who is allowed to talk to whom, as one pure table.
 *
 * A Trystero room is a mesh: everybody who joins connects to everybody else. This game is a star,
 * because the host runs the authoritative simulation and a guest has nothing to say to another
 * guest. Rather than build the mesh and then ignore most of it, the pairs that should not exist are
 * refused during the handshake, before the peer is ever announced. A refused handshake tears that
 * peer connection down, so a guest ends up holding exactly one connection: the host's.
 *
 * The same table settles collisions. Nothing prevents two people generating the same join code, and
 * nothing on a relay could, so the one who has held it longer keeps it and the other is refused
 * with a sentence saying so. Holding time is the rule rather than who claimed first, because
 * relays are slow enough that both sides can settle before either hears the other, and the
 * newcomer is then the one who should give way. Exactly equal holding times fall back to
 * `isInitiator`, which Trystero computes as `selfId < peerId` on both sides, so both reach the
 * same answer with no server and no round trip.
 *
 * The four-player cap is settled here too, for the same reason: a fifth guest that becomes a peer
 * has already cost a peer connection, a data channel and two watchers, and the game's roster admits
 * any guest peer it is handed, so it would be given a body before anything could take it away.
 * Refusing at the handshake means nothing downstream needs a filter. Both sides put their free seat
 * count in the hello, so the guest being turned away reaches the same verdict from its own copy of
 * this table rather than from an explanation the destroyed connection could never carry.
 *
 * A held seat is the one chair that count gets wrong. It is taken, and it is taken by exactly one
 * player, so a returning guest that proves the seat is its own is counted against that seat rather
 * than a fifth one. That proof is checked by the host alone, which is why a guest that made one
 * does not turn itself away on a hello that says the room is full: see
 * [seatProof](../wire/seatProof.ts).
 *
 * `yieldSession` is the difference between "this peer is not for me" and "I have lost this code".
 * The losing side of a collision yields, and so does a guest that found a full session: neither is
 * something another signaling strategy could rescue, and dialling the next one would only spend six
 * more seconds arriving at the same answer.
 */

import { negotiate, versionVerdictDetail } from '@/net/wire/protocolVersion';
import type { NetErrorCode } from '@/net/session/sessionErrors';
import type { RoleClaim } from '@/net/wire/helloFrame';
import type { SessionRole } from '@/net/transportContract';

export interface HandshakeInput {
  /** What this machine claims right now. A host claims `hostProbing` until its window passes. */
  readonly selfClaim: RoleClaim;
  readonly remoteClaim: RoleClaim;
  readonly remoteVersion: number;
  /** Trystero's `selfId < peerId`, the same on both sides. */
  readonly isInitiator: boolean;
  /** How long each side has held its claim on the code. */
  readonly selfHeldMs: number;
  readonly remoteHeldMs: number;
  /** True when this guest already accepted a host, so a second one is one too many. */
  readonly hasHost: boolean;
  /**
   * Seats this side can still offer this peer, which is not always what its hello said. A returning
   * guest that proved a held seat is its own is counted against that seat rather than a fifth.
   */
  readonly selfFreeSeats: number;
  /** What the other side put in its hello. */
  readonly remoteFreeSeats: number;
  /** True when this side put a claim on a held seat in its hello. A host never does. */
  readonly selfClaimsSeat: boolean;
}

export type HandshakeDecision =
  | { readonly accept: true; readonly peerRole: SessionRole }
  | {
      readonly accept: false;
      readonly code: NetErrorCode;
      readonly detail: string;
      /** True when this side has to give up the code, not just the peer. */
      readonly yieldSession: boolean;
    };

export function decideHandshake(input: HandshakeInput): HandshakeDecision {
  const verdict = negotiate(input.remoteVersion);
  if (verdict !== 'ok') {
    return refuse('versionMismatch', versionVerdictDetail(verdict, input.remoteVersion), false);
  }

  const remoteHosts = input.remoteClaim !== 'guest';

  if (input.selfClaim === 'guest') {
    if (!remoteHosts)
      return refuse('notMyHost', 'a guest does not connect to another guest', false);
    if (input.hasHost) return refuse('duplicateHost', 'this session already has a host', false);
    // Yielded, because a full session is full on every relay network there is. A guest that
    // claimed a seat is the exception: only the host holds the tokens, so only the host can say
    // whether the chair it is counting as taken is this player's own. Turning itself away on the
    // host's number would be answering a question it cannot see the answer to.
    if (input.remoteFreeSeats <= 0 && !input.selfClaimsSeat) {
      return refuse('sessionFull', 'the host has no free seat', true);
    }
    return { accept: true, peerRole: 'host' };
  }

  if (!remoteHosts) {
    if (input.selfFreeSeats <= 0) return refuse('sessionFull', 'this session is full', false);
    return { accept: true, peerRole: 'guest' };
  }

  // Two claims on one code. A settled host outranks one still probing; otherwise the older claim
  // wins, and an exact tie falls back to the tie-break both sides compute identically.
  const yieldSession = losesTheCode(input);

  return refuse(
    'codeAlreadyHosted',
    yieldSession
      ? 'another player is already hosting this code'
      : 'this code is already hosted here',
    yieldSession,
  );
}

function losesTheCode(input: HandshakeInput): boolean {
  const settled = input.selfClaim === 'host';
  const remoteSettled = input.remoteClaim === 'host';
  if (settled !== remoteSettled) return !settled;
  if (input.selfHeldMs !== input.remoteHeldMs) return input.selfHeldMs < input.remoteHeldMs;
  return !input.isInitiator;
}

function refuse(code: NetErrorCode, detail: string, yieldSession: boolean): HandshakeDecision {
  return { accept: false, code, detail, yieldSession };
}
